import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../aws-config';

// Types for file sharing
export interface Project {
  projectId: string;
  projectName: string;
  description: string;
  createdBy: string;
  createdAt: string;
  lastUpdated: string;
  fileCount: number;
}

export interface FileItem {
  fileId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  uploadedAt: string;
  uploadedBy: string;
  status: string;
  accessCount: number;
  lastAccessTime?: string;
  shareUrl?: string;
  expirationDate?: string;
  sharedAt?: string;
}

export interface UploadResponse {
  message: string;
  uploadUrl: string;
  fileId: string;
  s3Key: string;
  expiresIn: number;
}

export interface ShareResponse {
  message: string;
  downloadUrl: string;
  expiresAt: string;
  expiresIn: number;
  fileName: string;
  fileSize: number;
}

// Extended interface for file shares with expiration status
export interface FileShareStatus {
  url: string;
  expiresAt: string;
  isExpired: boolean;
  expiresInDays: number;
  expiresInHours: number;
  expirationStatus: 'expired' | 'expiring-soon' | 'active';
}

class FileSharingService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = API_URL;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('No authentication token found');
    }

    const url = `${this.apiUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Project management
  async getProjects(): Promise<{ projects: Project[]; total: number }> {
    return this.makeRequest('/file-sharing/projects');
  }

  async createProject(projectName: string, description: string = ''): Promise<{ message: string; project: Project }> {
    return this.makeRequest('/file-sharing/projects', {
      method: 'POST',
      body: JSON.stringify({
        projectName,
        description,
      }),
    });
  }

  async getProject(projectId: string): Promise<{ project: Project }> {
    return this.makeRequest(`/file-sharing/projects/${projectId}`);
  }

  // File operations
  async getProjectFiles(projectId: string): Promise<{ files: FileItem[]; total: number; projectId: string }> {
    return this.makeRequest(`/file-sharing/projects/${projectId}/files`);
  }

  async uploadFile(projectId: string, file: File): Promise<UploadResponse> {
    console.log('Starting file upload for:', file.name, 'Size:', file.size, 'Type:', file.type);
    
    // Ensure consistent Content-Type
    const contentType = file.type || 'application/octet-stream';
    console.log('Using content type:', contentType);
    
    // First, get the pre-signed URL
    const uploadData = await this.makeRequest(`/file-sharing/projects/${projectId}/files`, {
      method: 'POST',
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        contentType: contentType,
      }),
    });

    console.log('Got pre-signed URL, uploading to S3...');
    console.log('Upload URL (first 100 chars):', uploadData.uploadUrl.substring(0, 100));

    // Then upload the file to S3 using the pre-signed URL
    try {
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,  // Use the same content type
        },
      });

      console.log('S3 upload response status:', uploadResponse.status);
      console.log('S3 upload response headers:', Object.fromEntries(uploadResponse.headers.entries()));

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => 'Unknown error');
        console.error('S3 upload failed:', uploadResponse.status, uploadResponse.statusText, errorText);
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`);
      }

      console.log('File uploaded successfully to S3, marking as complete...');
      
      // Mark upload as complete
      try {
        await this.completeUpload(projectId, uploadData.fileId);
        console.log('Upload marked as complete');
      } catch (completeError) {
        console.error('Error marking upload as complete:', completeError);
        // Don't throw error here - S3 upload succeeded, just log the completion failure
      }
      
      return uploadData;
      
    } catch (uploadError) {
      console.error('Error during S3 upload:', uploadError);
      throw new Error(`File upload to S3 failed: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
    }
  }

  async completeUpload(projectId: string, fileId: string): Promise<void> {
    // Use the new simplified endpoint for more reliable CORS handling
    return this.makeRequest(`/file-sharing/complete-upload`, {
      method: 'PUT',
      body: JSON.stringify({
        projectId,
        fileId
      }),
    });
  }

  async getFileDetails(projectId: string, fileId: string): Promise<{ file: FileItem }> {
    return this.makeRequest(`/file-sharing/projects/${projectId}/files/${fileId}`);
  }

  async generateShareUrl(projectId: string, fileId: string, expirationDays: number = 7): Promise<ShareResponse> {
    return this.makeRequest(`/file-sharing/projects/${projectId}/files/${fileId}/share`, {
      method: 'POST',
      body: JSON.stringify({
        expirationDays,
      }),
    });
  }

  // Utility functions
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  // File sharing expiration status methods
  checkShareExpiration(expiresAt: string): FileShareStatus {
    try {
      const expirationDate = new Date(expiresAt);
      const now = new Date();
      
      // Check if already expired
      const isExpired = now > expirationDate;
      
      // Calculate time remaining
      const diffMs = expirationDate.getTime() - now.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      // Determine status
      let expirationStatus: 'expired' | 'expiring-soon' | 'active' = 'active';
      if (isExpired) {
        expirationStatus = 'expired';
      } else if (diffDays < 1) {
        expirationStatus = 'expiring-soon';
      }
      
      return {
        url: '',  // Will be set by caller
        expiresAt,
        isExpired,
        expiresInDays: diffDays < 0 ? 0 : diffDays,
        expiresInHours: diffHours < 0 ? 0 : diffHours,
        expirationStatus
      };
    } catch (error) {
      console.error('Error checking share expiration:', error);
      // Return a default state if date parsing fails
      return {
        url: '',
        expiresAt,
        isExpired: false,
        expiresInDays: 0,
        expiresInHours: 0,
        expirationStatus: 'active'
      };
    }
  }
  
  getExpirationDisplay(shareStatus: FileShareStatus): string {
    if (shareStatus.isExpired) {
      return `Expired`;
    } else if (shareStatus.expirationStatus === 'expiring-soon') {
      return shareStatus.expiresInHours > 0 
        ? `Expires in ${shareStatus.expiresInHours} hours` 
        : `Expires soon`;
    } else {
      return `Expires in ${shareStatus.expiresInDays} days`;
    }
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    // Define icon mappings
    const iconMap: Record<string, string> = {
      // Documents
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'txt': '📝',
      'rtf': '📝',
      
      // Spreadsheets
      'xls': '📊',
      'xlsx': '📊',
      'csv': '📊',
      
      // Presentations
      'ppt': '📽️',
      'pptx': '📽️',
      
      // Images
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'bmp': '🖼️',
      'svg': '🖼️',
      
      // Videos
      'mp4': '🎥',
      'avi': '🎥',
      'mov': '🎥',
      'wmv': '🎥',
      
      // Audio
      'mp3': '🎵',
      'wav': '🎵',
      'flac': '🎵',
      
      // Archives
      'zip': '📦',
      'rar': '📦',
      '7z': '📦',
      'tar': '📦',
      
      // Code
      'js': '💻',
      'ts': '💻',
      'html': '💻',
      'css': '💻',
      'py': '💻',
      'java': '💻',
      'cpp': '💻',
      'c': '💻',
    };

    return iconMap[extension] || '📄';
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }
}

export const fileSharingService = new FileSharingService();
